import {
  ArticulationCreditItem,
  CreditBookmark,
  CreditType,
  DynamicCreditItem,
  PrecedentCreditItem,
} from 'types/credit';
import { removeDuplicates, safeFilter } from 'utils/common';

import { Articulation } from 'types/articulation';
import { Course } from 'types/course';
import { FormSchema } from 'types/form-schema';
import { Precedent } from 'types/precedent';
import { SourceInstitution } from 'types/source-institution';
import _uniqBy from 'lodash/uniqBy';
import { getTotalCreditPoints as articulationCP } from 'utils/articulation';
import { extractSourceInstitutionName } from 'utils/required-credentials';
import { getHeadingAndSubHeading } from 'utils/required-credential';
import { getTotalCreditPoints as precedentCP } from 'utils/precedent';

/**
 * Sorts a list of credit items and returns a new list of credit items.
 * @param {DynamicCreditItem} creditItemA
 * @param {DynamicCreditItem} creditItemB
 * @param {FormSchema} formSchema
 * @return {*}  {number}
 */
function sortCreditItems(
  creditItemA: DynamicCreditItem,
  creditItemB: DynamicCreditItem,
  formSchema: FormSchema,
): number {
  // Articulations before Precedents
  if (creditItemA.type === CreditType.ARTICULATION && creditItemB.type === CreditType.PRECEDENT) {
    return -1;
  }
  if (creditItemA.type === CreditType.PRECEDENT && creditItemB.type === CreditType.ARTICULATION) {
    return 1;
  }
  // If both credit items are Articulations
  if (creditItemA.type === CreditType.ARTICULATION && creditItemB.type === CreditType.ARTICULATION) {
    const [articulationA, articulationB] = [creditItemA.articulation, creditItemB.articulation];
    const [aCP, bCP] = [articulationCP(articulationA), articulationCP(articulationB)];
    const aGuaranteedEntry = articulationA.arrangements.some((arrangement) => arrangement.guaranteedEntryOnly);
    const bGuaranteedEntry = articulationB.arrangements.some((arrangement) => arrangement.guaranteedEntryOnly);
    // Guaranteed entry pathways with zero credit points || same credit points
    if ((aGuaranteedEntry && aCP === 0 && bGuaranteedEntry && bCP === 0) || aCP === bCP) {
      return articulationA.course.name.toLowerCase().localeCompare(articulationB.course.name.toLowerCase());
    }
    if (aGuaranteedEntry && aCP === 0) {
      return -1;
    }
    if (bGuaranteedEntry && bCP === 0) {
      return 1;
    }
    return aCP > bCP ? 1 : -1;
  }
  // If both credits are Precedents
  if (creditItemA.type === CreditType.PRECEDENT && creditItemB.type === CreditType.PRECEDENT) {
    const [precedentA, precedentB] = [creditItemA.precedent, creditItemB.precedent];
    const [aCP, bCP] = [precedentCP(precedentA), precedentCP(precedentB)];
    if (aCP === bCP) {
      // Sort on precedent credential heading names
      const aHeadings = creditItemA.precedent.requiredCredentials
        .map((credential) => {
          const { heading } = getHeadingAndSubHeading(credential, formSchema);
          return heading;
        })
        .join(', ');
      const bHeadings = creditItemB.precedent.requiredCredentials
        .map((credential) => {
          const { heading } = getHeadingAndSubHeading(credential, formSchema);
          return heading;
        })
        .join(', ');
      return aHeadings.toLowerCase().localeCompare(bHeadings.toLowerCase());
    }
    return aCP > bCP ? 1 : -1;
  }
  return 0;
}

/**
 * Groups credit bookmarks by source institution.
 * @export
 * @param {Map<string, CreditBookmark>} creditBookmarks
 * @param {Map<string, Articulation>} articulations
 * @param {Map<string, Precedent>} precedents
 * @param {FormSchema} formSchema
 * @return {*}  {Array<[string, DynamicCreditItem[]]>}
 */
export function groupBySourceInstitutions(
  creditBookmarks: Map<string, CreditBookmark>,
  articulations: Map<string, Articulation>,
  precedents: Map<string, Precedent>,
  formSchema: FormSchema,
  sourceInstitutions: Map<string, SourceInstitution>,
): Array<[string, DynamicCreditItem[]]> {
  const grouped = Array.from(creditBookmarks.values()).reduce<Map<string, DynamicCreditItem[]>>((map, bookmark) => {
    const { creditItemID, creditType } = bookmark;
    if (creditType === CreditType.ARTICULATION) {
      const articulation = articulations.get(creditItemID);
      if (articulation) {
        // prettier-ignore
        const sourceInstitutionNames =  removeDuplicates(safeFilter(articulation.requiredCredentials.map((cred) => extractSourceInstitutionName(cred, sourceInstitutions)))).join(', ');
        const creditItem: ArticulationCreditItem = { type: CreditType.ARTICULATION, articulation };
        const existingEntry = map.get(sourceInstitutionNames);
        if (existingEntry) {
          existingEntry.push(creditItem);
        } else {
          map.set(sourceInstitutionNames, [creditItem]);
        }
      }
    }
    if (bookmark.creditType === CreditType.PRECEDENT) {
      const precedent = precedents.get(creditItemID);
      if (precedent) {
        // prettier-ignore
        const sourceInstitutionNames = removeDuplicates(safeFilter(precedent.requiredCredentials.map((cred) => extractSourceInstitutionName(cred, sourceInstitutions)))).join(', ');
        const creditItem: PrecedentCreditItem = { type: CreditType.PRECEDENT, precedent };
        const existingEntry = map.get(sourceInstitutionNames);
        if (existingEntry) {
          existingEntry.push(creditItem);
        } else {
          map.set(sourceInstitutionNames, [creditItem]);
        }
      }
    }
    return map;
  }, new Map());
  grouped.forEach((group) => group.sort((a, b) => sortCreditItems(a, b, formSchema)));
  // Sort map keys by source institution display name alphabetically (Map() maintains insertion order).
  return Array.from(grouped.entries()).sort(([aName], [bName]) =>
    aName.toLowerCase().localeCompare(bName.toLowerCase()),
  );
}

/**
 * Groups credit bookmarks by course.
 * @export
 * @param {Map<string, CreditBookmark>} creditBookmarks
 * @param {Map<string, Articulation>} articulations
 * @param {Map<string, Precedent>} precedents
 * @param {FormSchema} formSchema
 * @return {*}  {Map<Course, DynamicCreditItem[]>}
 */
export function groupByCourse(
  creditBookmarks: Map<string, CreditBookmark>,
  articulations: Map<string, Articulation>,
  precedents: Map<string, Precedent>,
  formSchema: FormSchema,
  groupBy: keyof Course,
): Array<[string, DynamicCreditItem[]]> {
  const grouped = Array.from(creditBookmarks.values()).reduce<Map<string, DynamicCreditItem[]>>((map, bookmark) => {
    const { creditItemID } = bookmark;

    const courses: Course[] = [];
    let creditItem: DynamicCreditItem | undefined = undefined;

    if (bookmark.creditType === CreditType.ARTICULATION) {
      const articulation = articulations.get(creditItemID);
      if (articulation) {
        creditItem = { type: CreditType.ARTICULATION, articulation };
        courses.push(articulation.course);
      }
    } else if (bookmark.creditType === CreditType.PRECEDENT) {
      const precedent = precedents.get(creditItemID);
      if (precedent) {
        creditItem = { type: CreditType.PRECEDENT, precedent };
        const deDupedCourses = _uniqBy(precedent.courses, groupBy);
        courses.push(...deDupedCourses);
      }
    }

    if (creditItem) {
      for (const course of courses) {
        const foundKey = Array.from(map.keys()).find((c) => c === course[groupBy]);
        if (foundKey) {
          const existingEntry = map.get(foundKey);
          if (existingEntry) {
            existingEntry.push(creditItem);
          }
        } else {
          map.set(course[groupBy] as string, [creditItem]);
        }
      }
    }
    return map;
  }, new Map());

  grouped.forEach((group) => group.sort((a, b) => sortCreditItems(a, b, formSchema)));
  return Array.from(grouped.entries()).sort(([aName], [bName]) =>
    aName.toLowerCase().localeCompare(bName.toLowerCase()),
  );
}
